/**
 * Backend client — wraps /api/collect/* endpoints.
 *
 * By default the requests go to this site's own origin (serverUrl
 * SAME_ORIGIN → base ''): the hosting proxies /api/collect/* to the HIVE
 * backend (vercel.json rewrite; Vite proxy in dev), so the browser never
 * talks to — or shows — the backend's own address. A self-hosted server can
 * be set under Settings → Advanced. Auth: `Authorization: Bearer <Trust
 * access token>` when signed in with Ordinal Pass (token held in memory,
 * lib/session.ts) and the base passes apiOriginAllowed (same origin, or an
 * allow-listed origin), otherwise the X-Collect-API-Key header.
 */
import type { Capsule, CapsuleBundle, CapsuleListResponse, HealthResponse, Settings } from '../types';
import {
  describeConnectionError,
  diagnoseFetchFailure,
  failureMessage,
  hostOf,
  normalizeBaseUrl,
  statusFailure,
  type ConnectionAudience,
} from './connection.ts';
import { apiOriginAllowed } from './oidc.ts';

export class CollectClient {
  // Plain fields, not parameter properties: tests load this file with Node's type stripping.
  private settings: Settings;
  private accessToken?: string;

  constructor(settings: Settings, accessToken?: string) {
    this.settings = settings;
    this.accessToken = accessToken;
  }

  private authHeaders(): Record<string, string> {
    if (this.accessToken && apiOriginAllowed(this.base, this.pageOrigin())) {
      return { Authorization: `Bearer ${this.accessToken}` };
    }
    if (this.settings.apiKey) return { 'X-Collect-API-Key': this.settings.apiKey };
    return {};
  }

  /** Normalized backend base ('' = this site: the Vercel rewrite, or the Vite proxy in dev). */
  private get base(): string {
    return normalizeBaseUrl(this.settings.serverUrl);
  }

  private pageOrigin(): string {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }

  /**
   * fetch() rejected (no HTTP response). Tell a CORS block apart from a dead
   * host with one no-cors probe of the public health route (see connection.ts).
   */
  private async networkError(): Promise<ApiError> {
    const origin = this.pageOrigin();
    // Same origin (Collect's own server): a CORS block cannot happen, only "unreachable".
    const kind = this.base ? await diagnoseFetchFailure(`${this.base}/api/collect/health`) : 'unreachable';
    return new ApiError(
      kind === 'cors' ? 'CORS_BLOCKED' : 'NETWORK',
      failureMessage(kind, this.base ? hostOf(this.base, origin) : '', origin),
      0,
    );
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    // base '' = relative URL: same origin (Vercel rewrite in production, Vite proxy in dev)
    const url = `${this.base}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init?.headers as Record<string, string> || {}),
      ...this.authHeaders(),
    };

    let res: Response;
    try {
      res = await fetch(url, { ...init, headers });
    } catch {
      throw await this.networkError();
    }

    const contentType = res.headers.get('content-type') || '';
    let body: any;
    try {
      body = await res.json();
    } catch {
      body = null;
    }

    if (!res.ok) throw httpError(res, body);

    // A 200 that is not JSON is a web page (e.g. the SPA's index.html when a
    // self-hosted address points at a website), never a Collect API answer.
    if (body === null || !/json/i.test(contentType)) {
      const origin = this.pageOrigin();
      throw new ApiError(
        'NOT_JSON',
        this.base
          ? `${hostOf(this.base, origin)} answered with ${contentType.split(';')[0] || 'no content type'}, not a Collect server. Check the server address under Advanced.`
          : 'The Collect server sent an unexpected answer. Try again in a minute.',
        res.status,
      );
    }

    return body as T;
  }

  /**
   * Login / Settings check: public health first, then an authenticated list.
   * Never throws; the message is worded for the given screen.
   */
  async testConnection(audience: ConnectionAudience): Promise<{ ok: boolean; code: string; message: string }> {
    try {
      await this.health();
    } catch (e: any) {
      return { ok: false, code: e?.code || 'ERROR', message: describeConnectionError(e || {}, audience) };
    }
    try {
      await this.listCapsules();
      // No service name from the health answer: it is an internal name.
      return { ok: true, code: 'OK', message: 'Connected. The access key was accepted.' };
    } catch (e: any) {
      return { ok: false, code: e?.code || 'ERROR', message: describeConnectionError(e || {}, audience) };
    }
  }

  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/api/collect/health');
  }

  async listCapsules(workspaceId?: string): Promise<CapsuleListResponse> {
    const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return this.request<CapsuleListResponse>(`/api/collect/capsule${qs}`);
  }

  async getCapsule(id: number): Promise<Capsule> {
    return this.request<Capsule>(`/api/collect/capsule/${id}`);
  }

  /**
   * Send a pre-built capsule (bundle + hash + gzipped payload) to the
   * backend. The bundleBuilder helpers in lib/bundleBuilder.ts produce
   * the shape this expects.
   */
  async sendCapsule(input: {
    workspaceId: string;
    manifest: Record<string, unknown>;
    payloadB64: string;
    contentHash: string;
    contentSize: number;
    sourceType?: string;
    tags?: string;
  }): Promise<{ capsule_id: number; name: string; status: string; storage_backend: string; storage_ref: string }> {
    return this.request('/api/collect/capsule', {
      method: 'POST',
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        manifest: input.manifest,
        payload_b64: input.payloadB64,
        content_hash: input.contentHash,
        content_size: input.contentSize,
        schema_type: 'collect.capsule.v1',
        source_type: input.sourceType || 'collect-web',
        tags: input.tags || `collect,workspace:${input.workspaceId},web-upload`,
      }),
    });
  }

  /**
   * Fetch the capsule payload from the backend, detect format
   * (gzip+b64 OR ml-kem-768 envelope), decompress + hash-verify if
   * unencrypted, OR throw ENCRYPTED with the envelope metadata so the
   * caller can surface decrypt instructions.
   *
   * Browser-side ML-KEM decryption is staged for a future commit (needs
   * WASM ML-KEM lib like liboqs-wasm). For now, encrypted capsules are
   * decrypted via `python collect.py keys decrypt-capsule …`.
   */
  async getCapsulePayload(id: number): Promise<CapsuleBundle> {
    const url = `${this.base}/api/collect/capsule/${id}/payload`;
    const headers: Record<string, string> = this.authHeaders();

    let res: Response;
    try {
      res = await fetch(url, { headers });
    } catch {
      throw await this.networkError();
    }
    if (!res.ok) {
      let body: any = null;
      try { body = await res.json(); } catch { /* ignore */ }
      throw httpError(res, body);
    }

    const expectedHash = res.headers.get('x-collect-content-hash') || '';
    const blob = new Uint8Array(await res.arrayBuffer());

    // Detect format. ML-KEM envelope is a JSON object starting with {"v":1,...
    // Gzip starts with magic bytes 1f 8b. Anything else = unknown.
    if (blob[0] === 0x7b /* '{' */) {
      // Try parse as ML-KEM envelope
      try {
        const text = new TextDecoder().decode(blob);
        const env = JSON.parse(text);
        if (env && typeof env === 'object' && env.v === 1 && typeof env.alg === 'string' && env.alg.startsWith('ml-kem-')) {
          throw new ApiError(
            'ENCRYPTED',
            JSON.stringify({
              alg: env.alg,
              ek_fp: env.ek_fp,
              capsuleId: id,
            }),
            0,
          );
        }
      } catch (e: any) {
        if (e instanceof ApiError) throw e;
        // Fall through — wasn't a valid envelope
      }
    }

    // Gzip path (legacy unencrypted)
    let raw: Uint8Array;
    try {
      const ds = new DecompressionStream('gzip');
      const writer = ds.writable.getWriter();
      writer.write(blob);
      writer.close();
      raw = new Uint8Array(await new Response(ds.readable).arrayBuffer());
    } catch (e: any) {
      throw new ApiError('DECOMPRESSION_FAILED', `gzip decode failed: ${e.message || e}`, 0);
    }

    // Verify SHA-256 against the server-declared hash
    if (expectedHash) {
      const buf = await crypto.subtle.digest('SHA-256', raw);
      const actualHash = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
      if (actualHash !== expectedHash) {
        throw new ApiError(
          'HASH_MISMATCH',
          `Content hash mismatch: expected ${expectedHash.slice(0, 12)}…, got ${actualHash.slice(0, 12)}…`,
          0,
        );
      }
    }

    let parsed: CapsuleBundle;
    try {
      parsed = JSON.parse(new TextDecoder().decode(raw));
    } catch (e: any) {
      throw new ApiError('JSON_PARSE_FAILED', `bundle JSON invalid: ${e.message || e}`, 0);
    }
    return parsed;
  }
}

/**
 * Non-2xx answer → ApiError. A Collect error body ({ error: { code, message } })
 * wins, except 413: a body too large for the proxy or the server always gets
 * the plain size message. Gateway pages (502–504 without a Collect body) get a
 * "try again" message instead of the proxy's own text.
 */
function httpError(res: Response, body: any): ApiError {
  const generic = statusFailure(res.status);
  if (res.status === 413 && generic) return new ApiError(generic.code, generic.message, res.status);
  if (body?.error?.code) return new ApiError(body.error.code, body.error.message || res.statusText || 'request failed', res.status);
  if (generic) return new ApiError(generic.code, generic.message, res.status);
  return new ApiError(`HTTP_${res.status}`, res.statusText || 'request failed', res.status);
}

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}
